import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { catchError, Observable, throwError, of } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class HomeService {
  serverEndpoint = environment.apiUrl;
  
  // External APIs
  public apiUrlForLivePriceFromDb = 'https://liverates-api.goldcentral.in/api/liveRateDB';
  public apiUrlForLivePriceForHistory = 'https://liverates-api.goldcentral.in/api/';

  // Token keys to check in order of priority
  private readonly TOKEN_KEYS = [
    'user-access',
    'token',
    'user-token-goldBharatFirst'
  ];
  baseUrl: any;

  constructor(
    private http: HttpClient,
    private router: Router
  ) { }

  /**
   * Get auth token from localStorage with validation
   */
  private getAuthToken(): string | null {
    for (const key of this.TOKEN_KEYS) {
      const token = localStorage.getItem(key);
      if (token && token !== 'undefined' && token !== 'null') {
        // Ensure token has Bearer prefix
        return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      }
    }
    return null;
  }

  /**
   * Get user ID from token or localStorage
   */
  private getUserId(): string | null {
    // Try to get from token first
    const token = this.getAuthToken();
    if (token) {
      try {
        const cleanToken = token.replace('Bearer ', '');
        const decodedToken: any = jwtDecode(cleanToken);
        if (decodedToken?.id) {
          return decodedToken.id.toString();
        }
        if (decodedToken?.userId) {
          return decodedToken.userId.toString();
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }

    // Fallback to localStorage
    const userDataString = localStorage.getItem('userData');
    if (userDataString && userDataString !== 'undefined') {
      try {
        const userData = JSON.parse(userDataString);
        if (userData?.id) {
          return userData.id.toString();
        }
      } catch (e) {
        console.error('Error parsing userData:', e);
      }
    }

    // Try direct user-id
    const userId = localStorage.getItem('user-id');
    if (userId && userId !== 'undefined' && userId !== 'null') {
      return userId;
    }

    return null;
  }

  /**
   * Create headers with authorization token
   */
  private createHeaders(): HttpHeaders {
    const token = this.getAuthToken();
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (token) {
      headers = headers.set('Authorization', token);
    }

    return headers;
  }

  /**
   * Handle API errors
   */
  private handleError(error: any): Observable<never> {
    console.error('API Error:', error);
    
    if (error.status === 401 || error.status === 403) {
      console.log('Authentication error - clearing session');
      this.clearSession();
    }
    
    return throwError(() => error);
  }

  /**
   * Clear all session data
   */
  private clearSession(): void {
    const keysToRemove = [
      ...this.TOKEN_KEYS,
      'userData',
      'user-details-goldBharat',
      'user-id',
      'user-name',
      'user-rolename',
      'user-permissions',
      'user-menu'
    ];
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Redirect to login if not already there
    if (!this.router.url.includes('/login')) {
      this.router.navigate(['/login']);
    }
  }

  /**
   * Build complete URL with proper formatting
   */
  private buildUrl(endpoint: string): string {
    // Remove leading slash if present
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    
    // Ensure serverEndpoint doesn't end with slash
    const baseUrl = this.serverEndpoint.endsWith('/') 
      ? this.serverEndpoint.slice(0, -1) 
      : this.serverEndpoint;
    
    return `${baseUrl}/${cleanEndpoint}`;
  }

  // ==================== Public API Methods ====================

  /**
   * Get live rates from external API
   */
  getLivePrice() {
    return this.http.get(this.apiUrlForLivePriceFromDb).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Book gold and silver
   */
  bookGoldAndSilver(data: any) {
    const url = this.buildUrl('booking');
    return this.http.post(url, data, { headers: this.createHeaders() }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Buy gold and silver
   */
  buyGoldAnsSilver(data: any) {
    const url = this.buildUrl('order');
    return this.http.post(url, data, { headers: this.createHeaders() }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Post KYC data
   */
  kycPost(data: any) {
    const url = this.buildUrl('KYCupdate');
    return this.http.post(url, data, { headers: this.createHeaders() }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Upload multiple images
   */
  imgUpload(data: any) {
    const url = this.buildUrl('multipleImageUpload');
    // Don't set Content-Type for FormData, browser will set it with boundary
    const headers = this.createHeaders().delete('Content-Type');
    return this.http.post(url, data, { headers }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Upload single image
   */
  singleImgUpload(data: any) {
    const url = this.buildUrl('imageupload');
    // Don't set Content-Type for FormData
    const headers = this.createHeaders().delete('Content-Type');
    return this.http.post(url, data, { headers }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Get about us content
   */
  aboutUs() {
    const url = this.buildUrl('aboutus');
    return this.http.get(url, { headers: this.createHeaders() }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Get orders by type
   */
ordersGet(type: string, page: number = 1, limit: number = 10) {

  // const url = this.buildUrl(`getMyOrders/${type}?page=${page}&limit=${limit}`);
  

  const url = this.buildUrl(`getMyOrders/${type}`);
  const params = new HttpParams()
    .set('page', page.toString())
    .set('limit', limit.toString());
    
  return this.http.get(url, { headers: this.createHeaders(), params }).pipe(
    catchError(err => this.handleError(err))
  );
}

//  ordersGet(type: string) {
//     const url = this.buildUrl(`getMyOrders/${type}`);
//     return this.http.get(url, { headers: this.createHeaders() }).pipe(
//       catchError(err => this.handleError(err))
//     );
//   }

  /**
   * Update receipt
   */
  // receiptUpdate(data: any) {
  //   const url = this.buildUrl('upload-transaction');
  //   return this.http.post(url, data, { headers: this.createHeaders() }).pipe(
  //     catchError(err => this.handleError(err))
  //   );
  // }

  receiptUpdate(data: FormData) {

  const token = this.getAuthToken();

  let headers = new HttpHeaders();

  if (token) {
    headers = headers.set('Authorization', token);
  }

  return this.http.post(
    this.buildUrl('upload-transaction'),
    data,
    { headers }
  );
}

  /**
   * Get gold limit
   */
  getGoldLimit() {
    const url = this.buildUrl('auth/getMaxLimit');
    return this.http.get(url, { headers: this.createHeaders() }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Get silver limit
   */
  getSilverLimit() {
    const url = this.buildUrl('auth/getSilverMaxLimit');
    return this.http.get(url, { headers: this.createHeaders() }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Post alert rate
   */
  postAlertrate(data: any) {
    const url = this.buildUrl('alert');
    return this.http.post(url, data, { headers: this.createHeaders() }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Get margin
   */
  getMargin() {
    const url = this.buildUrl('getMargins');
    return this.http.get(url, { headers: this.createHeaders() }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Get silver margin
   */
  silverGetMargin() {
    const url = this.buildUrl('getSilverMargins');
    return this.http.get(url, { headers: this.createHeaders() }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Get chart data
   */
  chartData(data: any) {
    const url = this.buildUrl('recordsByTime');
    return this.http.post(url, data, { headers: this.createHeaders() }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Update order status
   */
  updateOrderStatus(data: any) {
    const url = this.buildUrl('changeOrderStatus');
    return this.http.post(url, data, { headers: this.createHeaders() }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Get user KYC
   */
  getUserKyc(): Observable<any> {
    const url = this.buildUrl('getKyc');
    return this.http.get(url, { headers: this.createHeaders() }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Get delivery message by ID
   */
  deliveryMsg(id: number) {
    const url = this.buildUrl(`getDeliveryDetails/${id}`);
    return this.http.get(url, { headers: this.createHeaders() }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Post user new person details
   */
  // userNewPersonDetailspost(data: any) {
  //   const url = this.buildUrl('deliveryOtherPerson');
  //   return this.http.post(url, data, { headers: this.createHeaders() }).pipe(
  //     catchError(err => this.handleError(err))
  //   );
  // }


userNewPersonDetailspost(data: any) {
  const url = this.buildUrl('deliveryOtherPerson');
  const token = localStorage.getItem('authorization token');   
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`
  });
  return this.http.post(url, data, { headers }).pipe(
    catchError(err => this.handleError(err))
  );
}

  /**
   * Get notifications
   */
  getNotification(): Observable<any> {
    const url = this.buildUrl('notification');
    return this.http.get(url, { headers: this.createHeaders() }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Mark notifications as read
   */
  notificationRead() {
    const url = this.buildUrl('notifications/markAsRead');
    return this.http.post(url, {}, { headers: this.createHeaders() }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Get initial rates from external API
   */
  getInitialRates(): Observable<any> {
    return this.http.get<any>(this.apiUrlForLivePriceFromDb).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Get multiple gold records by days
   */
  getMultipleGoldRecords(days: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrlForLivePriceForHistory}/getMultipleGoldRecord/${days}`).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Check user active status - FIXED VERSION
   */
  checkUserStatus(): Observable<any> {
    const token = this.getAuthToken();
    const userId = this.getUserId();

    if (!token) {
      console.log('No token found for status check');
      return throwError(() => new Error('No authentication token found'));
    }

    if (!userId) {
      console.log('No user ID found for status check');
      return throwError(() => new Error('No user ID found'));
    }

    const url = this.buildUrl(`check-user-status/${userId}`);
    console.log('Checking user status at:', url);

    return this.http.get<any>(url, { headers: this.createHeaders() }).pipe(
      catchError((error) => {
        console.error('Error in checkUserStatus:', error);
        
        if (error.status === 401 || error.status === 403) {
          console.log('Session expired or invalid - clearing session');
          this.clearSession();
        }
        
        return throwError(() => error);
      })
    );
  }

  /**
   * Get timings by user ID
   */
  getTimings(userId: number): Observable<any> {
    const params = new HttpParams().set('userId', userId.toString());
    const url = this.buildUrl('timings/check-visibility');
    
    return this.http.get<any>(url, { 
      headers: this.createHeaders(),
      params 
    }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Set timings for user
   */
  seTimings(userId: string, weekTimings: any): Observable<any> {
    const url = this.buildUrl('settimings');
    const data = { userId, weekTimings };
    
    return this.http.post<any>(url, data, { headers: this.createHeaders() }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Logout user - clears session and redirects
   */
  logout() {
    this.clearSession();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }

  /**
   * Get current user details from localStorage
   */
  getCurrentUser(): any {
    const userDataString = localStorage.getItem('userData');
    if (userDataString && userDataString !== 'undefined') {
      try {
        return JSON.parse(userDataString);
      } catch {
        return null;
      }
    }
    return null;
  }



  //this is for gold coins and silver coins//
  getMakingCharges(metalType: string): Observable<any> {

  const url = this.buildUrl('getmakingcharges');

  const params = new HttpParams()
    .set('metal_type', metalType);

  return this.http.get(url, {
    headers: this.createHeaders(),
    params
  }).pipe(
    catchError(err => this.handleError(err))
  );

}

placeCoinOrder(data: any) {
  const url = this.buildUrl('buy-coins');

  return this.http.post(url, data, {
    headers: this.createHeaders()
  }).pipe(
    catchError(err => this.handleError(err))
  );
}



getCoinOrders(
  metalType: string,
  page: number = 1,
  limit: number = 10
) {

  const params = new HttpParams()
    .set('metal_type', metalType)
    .set('page', page.toString())
    .set('limit', limit.toString());

  return this.http.get(
    this.buildUrl('my-coin-orders'),
    {
      headers: this.createHeaders(),
      params
    }
  );
}

uploadCoinTransaction(data: FormData) {
  const token = this.getAuthToken();

  let headers = new HttpHeaders();

  if (token) {
    headers = headers.set('Authorization', token);
  }

  return this.http.post(
    this.buildUrl('upload-coin-transaction'),
    data,
    { headers }
  );
}


}